import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Autocomplete, MenuItem } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearBodegas = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    invcodigo: "",
    bodcodigo: "",
    boddescri: "",
    bodstatus: "A",
    bodserieblanco: 0,
    loccodigo: "",
  })

  // --- LÓGICA DEFENSIVA PARA INVENTARIOS ---
  const { data: rawInventarios, isLoading: isLoadingInventarios } = useQuery({
    queryKey: ["listaInventarios_Crear"],
    queryFn: async () => {
      try {
        const response = await api.get("/Bodegas/getListaInventarios")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Inventarios:", error)
        return []
      }
    },
  })

  const inventariosValidados = Array.isArray(rawInventarios)
    ? rawInventarios
    : Array.isArray(rawInventarios?.data)
      ? rawInventarios.data
      : []

  const listaInventarios = inventariosValidados.map((item) => ({
    id: item.invcodigo || item.id,
    label: item.label || `${item.invcodigo} - ${item.invdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  // --- LÓGICA DEFENSIVA PARA LOCALIDADES ---
  const { data: rawLocalidades, isLoading: isLoadingLocalidades } = useQuery({
    queryKey: ["listaLocalidades_Crear"],
    queryFn: async () => {
      try {
        const response = await api.get("/Bodegas/getListaLocalidad")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Localidades:", error)
        return []
      }
    },
  })

  const localidadesValidadas = Array.isArray(rawLocalidades)
    ? rawLocalidades
    : Array.isArray(rawLocalidades?.data)
      ? rawLocalidades.data
      : []

  const listaLocalidades = localidadesValidadas.map((item) => ({
    id: item.loccodigo || item.id,
    label: item.label || `${item.loccodigo} - ${item.locdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveCreacionBodega,
    isPending: isSavingCreacion,
    isError,
  } = useMutation({
    queryKey: ["isCreatingBodega"],
    fn: async (data) => {
      const response = await api.post("/Bodegas/createBodegas", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = typeof value === "string" ? value.toUpperCase() : value

    if (field === "bodcodigo") finalValue = finalValue.slice(0, 3)
    else if (field === "boddescri") finalValue = finalValue.slice(0, 100)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones del frontend basadas en las reglas de negocio (localidad ahora obligatoria)
    if (!formData.invcodigo.trim()) return showWarning("Debe seleccionar un Inventario")
    if (!formData.bodcodigo.trim()) return showWarning("El Código de la Bodega es obligatorio")
    if (!formData.boddescri.trim()) return showWarning("La Descripción de la Bodega es obligatoria")
    if (!formData.loccodigo.trim()) return showWarning("Debe seleccionar una Localidad")

    await SaveCreacionBodega(formData)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCreacion || isLoadingInventarios || isLoadingLocalidades}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nueva Bodega</b>
        </div>

        <CustomBackdrop isLoading={isSavingCreacion || isLoadingInventarios || isLoadingLocalidades} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de la Bodega
            </Typography>

            <Grid container spacing={3}>
              {/* COMBO INVENTARIO */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaInventarios}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaInventarios.find((c) => c.id === formData.invcodigo) ||
                    (formData.invcodigo ? { id: formData.invcodigo, label: `${formData.invcodigo}` } : null)
                  }
                  onChange={(event, newValue) =>
                    setFormData((prev) => ({ ...prev, invcodigo: newValue ? newValue.id : "" }))
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Inventario *"
                      placeholder="Buscar inventario..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.invcodigo}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO BODEGA */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código Bodega *"
                  value={formData.bodcodigo}
                  onChange={(e) => handleInputChange("bodcodigo", e.target.value)}
                  error={isError && !formData.bodcodigo}
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>

              {/* DESCRIPCIÓN BODEGA */}
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.boddescri}
                  onChange={(e) => handleInputChange("boddescri", e.target.value)}
                  error={isError && !formData.boddescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* ESTADO */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.bodstatus}
                  onChange={(e) => handleInputChange("bodstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>

              {/* SERIE BLANCO */}
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Serie Blanco"
                  type="number"
                  value={formData.bodserieblanco}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bodserieblanco: parseInt(e.target.value) || 0 }))}
                />
              </Grid>

              {/* COMBO LOCALIDAD (OBLIGATORIO) */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaLocalidades}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaLocalidades.find((l) => l.id === formData.loccodigo) ||
                    (formData.loccodigo ? { id: formData.loccodigo, label: `${formData.loccodigo}` } : null)
                  }
                  onChange={(event, newValue) =>
                    setFormData((prev) => ({ ...prev, loccodigo: newValue ? newValue.id : "" }))
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localidad *"
                      placeholder="Buscar localidad..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.loccodigo}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearBodegas
